/**
 * ステージレジストリ。
 *
 * 新しい都道府県 / モードを追加するときは
 *   1. stages/ に StageDefinition を作る
 *   2. この STAGES へ登録する
 * だけでよい（ゲームロジックの変更は不要）。
 */

import type { StageDefinition } from '../types';
import { aichiFoodStage } from './aichiFood';
import { akitaFoodStage } from './akitaFood';
import { aomoriFoodStage } from './aomoriFood';
import { chibaFoodStage } from './chibaFood';
import { ehimeFoodStage } from './ehimeFood';
import { fukuiFoodStage } from './fukuiFood';
import { fukuokaFoodStage } from './fukuokaFood';
import { fukushimaFoodStage } from './fukushimaFood';
import { gifuFoodStage } from './gifuFood';
import { gunmaFoodStage } from './gunmaFood';
import { hokkaidoFoodStage } from './hokkaidoFood';
import { hiroshimaFoodStage } from './hiroshimaFood';
import { hyogoFoodStage } from './hyogoFood';
import { ibarakiFoodStage } from './ibarakiFood';
import { ishikawaFoodStage } from './ishikawaFood';
import { iwateFoodStage } from './iwateFood';
import { kagawaFoodStage } from './kagawaFood';
import { kagoshimaFoodStage } from './kagoshimaFood';
import { kanagawaFoodStage } from './kanagawaFood';
import { kochiFoodStage } from './kochiFood';
import { kumamotoFoodStage } from './kumamotoFood';
import { kyotoFoodStage } from './kyotoFood';
import { mieFoodStage } from './mieFood';
import { miyagiFoodStage } from './miyagiFood';
import { miyazakiFoodStage } from './miyazakiFood';
import { naganoFoodStage } from './naganoFood';
import { nagasakiFoodStage } from './nagasakiFood';
import { naraFoodStage } from './naraFood';
import { niigataFoodStage } from './niigataFood';
import { oitaFoodStage } from './oitaFood';
import { okayamaFoodStage } from './okayamaFood';
import { okinawaFoodStage } from './okinawaFood';
import { osakaFoodStage } from './osakaFood';
import { sagaFoodStage } from './sagaFood';
import { saitamaFoodStage } from './saitamaFood';
import { shigaFoodStage } from './shigaFood';
import { shimaneFoodStage } from './shimaneFood';
import { shizuokaFoodStage } from './shizuokaFood';
import { tochigiFoodStage } from './tochigiFood';
import { tokushimaFoodStage } from './tokushimaFood';
import { tokyoFoodStage } from './tokyoFood';
import { tottoriFoodStage } from './tottoriFood';
import { toyamaFoodStage } from './toyamaFood';
import { wakayamaFoodStage } from './wakayamaFood';
import { yamagataFoodStage } from './yamagataFood';
import { yamaguchiFoodStage } from './yamaguchiFood';
import { yamanashiFoodStage } from './yamanashiFood';

/** ステージID -> 定義。キーは `<regionId>-<mode>`。 */
export const STAGES: Readonly<Record<string, StageDefinition>> = {
  'fukuoka-food': fukuokaFoodStage,
  'hokkaido-food': hokkaidoFoodStage,
  'miyagi-food': miyagiFoodStage,
  'chiba-food': chibaFoodStage,
  'hiroshima-food': hiroshimaFoodStage,
  'kagawa-food': kagawaFoodStage,
  'hyogo-food': hyogoFoodStage,
  'okinawa-food': okinawaFoodStage,
  'yamagata-food': yamagataFoodStage,
  'shizuoka-food': shizuokaFoodStage,
  'fukui-food': fukuiFoodStage,
  'saga-food': sagaFoodStage,
  'nagasaki-food': nagasakiFoodStage,
  'oita-food': oitaFoodStage,
  'miyazaki-food': miyazakiFoodStage,
  'kagoshima-food': kagoshimaFoodStage,
  'kumamoto-food': kumamotoFoodStage,
  'tottori-food': tottoriFoodStage,
  'shimane-food': shimaneFoodStage,
  'okayama-food': okayamaFoodStage,
  'yamaguchi-food': yamaguchiFoodStage,
  'tokushima-food': tokushimaFoodStage,
  'ehime-food': ehimeFoodStage,
  'kochi-food': kochiFoodStage,
  'kyoto-food': kyotoFoodStage,
  'osaka-food': osakaFoodStage,
  'shiga-food': shigaFoodStage,
  'nara-food': naraFoodStage,
  'wakayama-food': wakayamaFoodStage,
  'aichi-food': aichiFoodStage,
  'gifu-food': gifuFoodStage,
  'mie-food': mieFoodStage,
  'toyama-food': toyamaFoodStage,
  'ishikawa-food': ishikawaFoodStage,
  'niigata-food': niigataFoodStage,
  'aomori-food': aomoriFoodStage,
  'iwate-food': iwateFoodStage,
  'akita-food': akitaFoodStage,
  'fukushima-food': fukushimaFoodStage,
  'ibaraki-food': ibarakiFoodStage,
  'tochigi-food': tochigiFoodStage,
  'gunma-food': gunmaFoodStage,
  'saitama-food': saitamaFoodStage,
  'tokyo-food': tokyoFoodStage,
  'kanagawa-food': kanagawaFoodStage,
  'yamanashi-food': yamanashiFoodStage,
  'nagano-food': naganoFoodStage,
  // 例）今後の追加予定:
  // 'shonai-food': shonaiFoodStage,      // ご当地（parentId: 'yamagata-food'）
  // 'fukuoka-sightseeing': fukuokaSightseeingStage,
};

/** ステージ選択に出す（制作が終わっている）ステージID。 */
export const PUBLISHED_STAGE_IDS = Object.entries(STAGES)
  .filter(([, stage]) => !stage.draft)
  .map(([id]) => id);

export const STAGE_IDS = Object.keys(STAGES);

/**
 * ステージ定義から、レジストリのキー（ステージID）を導出する。
 *
 * `regionId` と `mode` の組み合わせが STAGES のキーと一致する規約。
 * セーブデータの保存先を分けるのにも使う。
 */
export function stageIdOf(stage: Pick<StageDefinition, 'regionId' | 'mode'>): string {
  return `${stage.regionId}-${stage.mode}`;
}

/** ステージIDから定義を取得する。存在しない場合は undefined。 */
export function getStage(stageId: string): StageDefinition | undefined {
  return STAGES[stageId];
}

/** ステージIDから定義を取得する。存在しない場合は先頭のステージへフォールバックする。 */
export function getStageOrFallback(stageId: string): StageDefinition {
  const stage = getStage(stageId);
  if (stage) return stage;
  const firstId = STAGE_IDS[0];
  return STAGES[firstId];
}

export {
  fukuokaFoodStage,
  hokkaidoFoodStage,
  yamagataFoodStage,
  miyagiFoodStage,
  chibaFoodStage,
  fukuiFoodStage,
  shizuokaFoodStage,
  hyogoFoodStage,
  hiroshimaFoodStage,
  kagawaFoodStage,
  okinawaFoodStage,
};
export { resolveStageIdFromPath, stagePath, isRootPath, DEFAULT_STAGE_MODE } from './routing';
